import React, { createContext, useEffect, useState, useMemo } from "react";
import { useKeycloak } from "@react-keycloak/web";
import get from "lodash/get";
import { useMutation } from "react-query";
import { keycloakapiUrls } from "misc/urls";
import { OrganizationRoles, RealmManagementRoles, UserManagementRoles } from "misc/roles";

import en_US from "antd/lib/locale/en_US";
import de_DE from "antd/lib/locale/de_DE";
import ja_JP from "antd/lib/locale/ja_JP";

const i18nLocales = {
    en: "en-US",
    de: "de-DE",
    jp: "jp-JP",
    ja: "jp-JP"
};

const momentLocales = {
    en: "en-us",
    de: "de",
    jp: "jp",
    ja: "jp"
};

const antdLocales = {
    en: en_US,
    de: de_DE,
    jp: ja_JP,
    ja: ja_JP
};
export const LoggedInUserContext = createContext(undefined);

const LoggedInUserProvider = ({ children }) => {
    const browserLanguage = navigator.language ?? navigator.userLanguage;
    const { keycloak } = useKeycloak();
    const [user, setUser] = useState(keycloak?.tokenParsed);
    const [locale, setLocale] = useState(browserLanguage.toLowerCase().slice(0, 2) ?? "de");
    const { mutate: mutateLocale, ...localeMutation } = useMutation();

    useEffect(() => {
        if (keycloak.authenticated) {
            const user = keycloak.tokenParsed;
            const locale = user.locale ?? "de";
            setUser(user);
            setLocale(locale);
        }
    }, [keycloak.authenticated]);

    const activeOrganization = useMemo(() => (user?.active_organization ? user.active_organization : {}), [user]);

    const checkOrgForRole = (orgId, role) => {
        const userOrgs = get(user, "organizations");
        const getRoles = get(userOrgs, [orgId, "roles"]);
        return getRoles ? getRoles.includes(role) : false;
    };

    const checkRealmManagementForRole = role => {
        const realmManagmentRoles = get(user, "resource_access.realm-management.roles", []);
        return realmManagmentRoles.includes(role);
    };

    const checkUserManagementForRole = role => {
        const userManagementRoles = get(user, "resource_access.usermanagement.roles", []);
        return userManagementRoles.includes(role);
    };

    const updateLocale = (locale, { onSuccess, ...props } = {}) => {
        return mutateLocale(
            {
                url: `${keycloakapiUrls.users()}${user?.sub}/set_locale/`,
                method: "PATCH",
                body: JSON.stringify({ locale: locale })
            },
            {
                onSuccess: newData => {
                    setLocale(newData.locale);
                },
                ...props
            }
        );
    };

    const hasAuthorityFull = () => checkUserManagementForRole(UserManagementRoles.PermissionAuthorityFull);
    const hasAuthorityLocalAdmin = () => checkUserManagementForRole(UserManagementRoles.PermissionAuthorityLocalAdmin);
    const hasOrgRoleManageOrganization = orgId => checkOrgForRole(orgId, OrganizationRoles.ManageOrganization);

    const contextValue = useMemo(
        () => ({
            keycloak,
            user,
            locale: {
                default: locale,
                i18n: i18nLocales[locale],
                moment: momentLocales[locale],
                antd: antdLocales[locale]
            },
            activeOrganization,
            updateLocale,
            localeMutation,
            checkOrgForRole,
            checkRealmManagementForRole,
            checkUserManagementForRole,
            hasAuthorityFull,
            hasAuthorityLocalAdmin,
            hasOrgRoleManageOrganization
        }),
        [user, locale, activeOrganization, localeMutation, updateLocale, checkOrgForRole, checkRealmManagementForRole, keycloak]
    );

    return <LoggedInUserContext.Provider value={contextValue}>{children}</LoggedInUserContext.Provider>;
};

export default LoggedInUserProvider;
